// static/table_engine_src/core/validation.js
import { dateParseOptionsForCol, isDateCol, normalizeDateValue } from "../util/date.js";

function isBlankValue(value) {
  return String(value ?? "").trim() === "";
}

function toRegExp(pattern) {
  if (!pattern) return null;
  if (pattern instanceof RegExp) return pattern;

  if (typeof pattern === "string") {
    try {
      return new RegExp(pattern);
    } catch {
      return null;
    }
  }
  return null;
}

function getEnumValues(col, validation) {
  if (Array.isArray(validation?.enum) && validation.enum.length) {
    return validation.enum.map((v) => String(v ?? ""));
  }
  if (Array.isArray(col?.enumValues) && col.enumValues.length) {
    return col.enumValues.map((v) => String(v ?? ""));
  }
  if (Array.isArray(col?.options) && col.options.length) {
    return col.options.map((o) =>
      (o && typeof o === "object" && o.value != null) ? String(o.value) : String(o ?? ""));
  }
  return [];
}

function parseNumericCell(ctx, el, col) {
  const parseNum = (value, fmt = null) =>
    (typeof ctx.parseNumber === "function" ? ctx.parseNumber(value, NaN, fmt) : Number(value));

  const raw = String(
    (typeof ctx.getAnyRawDataset === "function" ? ctx.getAnyRawDataset(el) : el?.dataset?.raw) ?? "",
  ).trim();
  const text = String(el?.value ?? "").trim();
  if (raw === "" && text === "") return { ok: true, blank: true, value: NaN };

  if (raw !== "") {
    const nRaw = parseNum(raw);
    if (Number.isFinite(nRaw)) return { ok: true, blank: false, value: nRaw };
  }

  const fmt = ctx.effectiveFormatForCol?.(col) || ctx.normalizeFormat?.(col) || null;
  const nDisp = parseNum(text, fmt);
  if (Number.isFinite(nDisp)) {
    const sc = ctx.colScale?.(col) ?? 1;
    return { ok: true, blank: false, value: nDisp / sc };
  }

  return { ok: false, blank: false, value: NaN };
}

function getInputByRowAndKey(ctx, rowIndex, colKey) {
  const rows = ctx.getRowEls?.() || [];
  const tr = rows[rowIndex];
  if (!tr) return null;
  return ctx.getInput?.(tr, colKey) || null;
}

function rowIndexForTr(ctx, tr) {
  if (!tr) return -1;

  const byData = parseInt(tr.dataset?.teRowIdx || "-1", 10);
  if (Number.isFinite(byData) && byData >= 0) return byData;

  const rows = ctx.getRowEls?.() || [];
  return rows.indexOf(tr);
}

function rowIndexForInput(ctx, inputEl) {
  const tr = inputEl?.closest?.("tr");
  return rowIndexForTr(ctx, tr);
}

function setInputValidationDecor(inputEl, { invalid, message = "" } = {}) {
  if (!inputEl || !inputEl.classList) return;

  const td = inputEl.closest?.("td");
  if (invalid) {
    inputEl.classList.add("te-cell-invalid");
    inputEl.dataset.teInvalid = "1";
    inputEl.setAttribute("aria-invalid", "true");

    if (message) {
      if (inputEl.dataset.teTitleBeforeInvalid === undefined) {
        inputEl.dataset.teTitleBeforeInvalid = String(inputEl.title || "");
      }
      inputEl.title = String(message);
    }

    if (td?.classList) td.classList.add("te-cell-error");
    return;
  }

  inputEl.classList.remove("te-cell-invalid");
  delete inputEl.dataset.teInvalid;
  inputEl.removeAttribute("aria-invalid");

  if (inputEl.dataset.teTitleBeforeInvalid !== undefined) {
    inputEl.title = String(inputEl.dataset.teTitleBeforeInvalid || "");
    delete inputEl.dataset.teTitleBeforeInvalid;
  }

  if (td?.classList) td.classList.remove("te-cell-error");
}

function buildError(rowIndex, col, message) {
  const header = String(col?.header || col?.key || "Field");
  return {
    rowIndex,
    rowNumber: rowIndex + 1,
    colKey: String(col?.key || ""),
    header,
    message: String(message || `${header} is invalid.`),
  };
}

function semanticType(col) {
  return String(col?.semanticType || col?.type || "text").toLowerCase();
}

function validateInputEl(ctx, inputEl, col, rowIndex, { commit = false } = {}) {
  const validation = col?.validation && typeof col.validation === "object" ? col.validation : null;
  const fieldLabel = String(col?.header || col?.key || "Field");

  const fail = (message) => ({
    ok: false,
    message: String(message || `${fieldLabel} is invalid.`),
  });

  const pass = () => ({ ok: true, message: "" });

  if (!inputEl || !col || col.kind !== "input") return pass();
  if (col.readonly || col.locked) return pass();

  const semType = semanticType(col);
  const rawText = String(inputEl.value ?? "").trim();
  const rawDataset = String(
    (typeof ctx.getAnyRawDataset === "function" ? ctx.getAnyRawDataset(inputEl) : inputEl?.dataset?.raw) ?? "",
  ).trim();
  const required = validation?.required === true;

  if (isBlankValue(rawText) && isBlankValue(rawDataset)) {
    if (!required) return pass();
    return fail(validation?.requiredMessage || `${fieldLabel} is required.`);
  }

  if (ctx.isNumericCol?.(col)) {
    const parsed = parseNumericCell(ctx, inputEl, col);
    if (!parsed.ok || !Number.isFinite(parsed.value)) {
      return fail(validation?.message || `${fieldLabel} must be a number.`);
    }

    const n = parsed.value;
    if (validation?.integer === true && !Number.isInteger(n)) {
      return fail(validation?.integerMessage || `${fieldLabel} must be an integer.`);
    }
    if (Number.isFinite(Number(validation?.min)) && n < Number(validation.min)) {
      return fail(validation?.minMessage || `${fieldLabel} must be >= ${validation.min}.`);
    }
    if (Number.isFinite(Number(validation?.max)) && n > Number(validation.max)) {
      return fail(validation?.maxMessage || `${fieldLabel} must be <= ${validation.max}.`);
    }
    if (Number.isFinite(Number(validation?.minExclusive)) && !(n > Number(validation.minExclusive))) {
      return fail(
        validation?.minExclusiveMessage || `${fieldLabel} must be > ${validation.minExclusive}.`,
      );
    }
    if (Number.isFinite(Number(validation?.maxExclusive)) && !(n < Number(validation.maxExclusive))) {
      return fail(
        validation?.maxExclusiveMessage || `${fieldLabel} must be < ${validation.maxExclusive}.`,
      );
    }
  } else if (isDateCol(col)) {
    const iso = normalizeDateValue(rawText, dateParseOptionsForCol(col));
    if (!iso) return fail(validation?.message || `${fieldLabel} must be YYYY-MM-DD.`);
    if (commit && String(inputEl.value || "") !== iso) inputEl.value = iso;
  } else if (semType === "enum" || col.type === "select") {
    const allowed = getEnumValues(col, validation);
    if (allowed.length && !allowed.includes(String(rawText))) {
      return fail(validation?.message || `${fieldLabel} must match one of the allowed values.`);
    }
  } else if (semType === "isin") {
    const v = col.autoUppercase === false ? rawText : rawText.toUpperCase();
    const isinPattern = /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/;
    if (!isinPattern.test(v)) {
      return fail(validation?.message || `${fieldLabel} must be a valid ISIN.`);
    }
    if (commit && v !== inputEl.value) inputEl.value = v;
  } else if (semType === "id") {
    if (/[\t\r\n]/.test(rawText)) {
      return fail(validation?.message || `${fieldLabel} cannot contain line breaks.`);
    }
  }

  if (validation) {
    const s = String(inputEl.value ?? "");
    if (Number.isFinite(Number(validation.minLength)) && s.length < Number(validation.minLength)) {
      return fail(validation.minLengthMessage || `${fieldLabel} is too short.`);
    }
    if (Number.isFinite(Number(validation.maxLength)) && s.length > Number(validation.maxLength)) {
      return fail(validation.maxLengthMessage || `${fieldLabel} is too long.`);
    }

    const rgx = toRegExp(validation.pattern);
    if (rgx && !rgx.test(s)) {
      return fail(validation.patternMessage || `${fieldLabel} has invalid format.`);
    }

    if (typeof validation.validator === "function") {
      const rowEl = inputEl.closest?.("tr");
      const rowData = rowEl ? (ctx.readRow?.(rowEl, { includeOutputs: true }) || {}) : {};
      const out = validation.validator({
        value: s,
        row: rowData,
        rowIndex,
        rowNumber: rowIndex + 1,
        col,
        input: inputEl,
        engine: ctx.__api || null,
      });
      if (out === false) return fail(validation.message || `${fieldLabel} is invalid.`);
      if (typeof out === "string" && out) return fail(out);
      if (out && typeof out === "object" && out.ok === false) {
        return fail(out.message || validation.message || `${fieldLabel} is invalid.`);
      }
    }
  }

  return pass();
}

export function installValidation(ctx) {
  if (!ctx || typeof ctx !== "object") return;
  const { table, cols, colByKey, hiddenCols } = ctx;
  if (!table || !Array.isArray(cols) || !colByKey) return;

  const shouldValidateCol = (col) => {
    if (!col || col.kind !== "input") return false;
    if (col.readonly || col.locked) return false;

    if (col.validation && typeof col.validation === "object" && Object.keys(col.validation).length) {
      return true;
    }

    const sem = semanticType(col);
    return (
      sem === "enum" ||
      sem === "isin" ||
      sem === "id" ||
      sem === "date" ||
      sem === "percent" ||
      sem === "currency"
    );
  };

  const validationKeys = new Set(
    cols
      .filter((c) => shouldValidateCol(c))
      .map((c) => c.key),
  );

  if (!validationKeys.size) {
    ctx.validateCell = () => ({ ok: true, errors: [] });
    ctx.validateRow = () => ({ ok: true, errors: [] });
    ctx.validateAll = () => ({ ok: true, errors: [] });
    ctx.validateForCommit = () => ({ ok: true, errors: [], firstError: null });
    ctx.hasValidationErrors = () => false;
    ctx.getValidationErrors = () => [];
    ctx.getFirstValidationError = () => null;
    ctx.focusValidationError = () => false;
    ctx.clearValidationErrors = () => {};
    return;
  }

  const on =
    typeof ctx.on === "function"
      ? ctx.on
      : (target, type, handler, options) => {
          if (!target || typeof target.addEventListener !== "function") return handler;
          if (typeof handler !== "function") return handler;
          target.addEventListener(type, handler, options);
          return handler;
        };

  // row element -> (colKey -> error descriptor)
  const errorsByRow = new Map();
  let lastSignature = "";
  let _renderReconcilePending = false;

  function normalizeErrorEntry(col, message) {
    return {
      colKey: String(col?.key || ""),
      header: String(col?.header || col?.key || "Field"),
      message: String(message || `${String(col?.header || col?.key || "Field")} is invalid.`),
    };
  }

  function clearStoredError(rowEl, colKey, inputEl = null) {
    if (!rowEl || !colKey) return false;
    const rowMap = errorsByRow.get(rowEl);
    const had = !!rowMap?.has?.(colKey);
    if (had) {
      rowMap.delete(colKey);
      if (!rowMap.size) errorsByRow.delete(rowEl);
    }
    if (inputEl) setInputValidationDecor(inputEl, { invalid: false });
    return had;
  }

  function pruneErrorStore() {
    const liveRows = new Set(ctx.getRowEls?.() || []);
    let changed = false;

    for (const [rowEl, rowMap] of errorsByRow) {
      if (!liveRows.has(rowEl)) {
        errorsByRow.delete(rowEl);
        changed = true;
        continue;
      }

      for (const [colKey] of rowMap) {
        const col = colByKey.get(colKey);
        if (!col || !validationKeys.has(colKey) || hiddenCols?.has?.(colKey)) {
          rowMap.delete(colKey);
          changed = true;
          const inputEl = ctx.getInput?.(rowEl, colKey);
          if (inputEl) setInputValidationDecor(inputEl, { invalid: false });
        }
      }

      if (!rowMap.size) {
        errorsByRow.delete(rowEl);
        changed = true;
      }
    }

    return changed;
  }

  function reapplyDecorFromStore() {
    for (const [rowEl, rowMap] of errorsByRow) {
      for (const entry of rowMap.values()) {
        const inputEl = ctx.getInput?.(rowEl, entry.colKey);
        if (!inputEl) continue;
        setInputValidationDecor(inputEl, { invalid: true, message: entry.message });
      }
    }
  }

  function getValidationErrors() {
    const out = [];
    for (const [rowEl, rowMap] of errorsByRow) {
      const rowIndex = rowIndexForTr(ctx, rowEl);
      if (!Number.isInteger(rowIndex) || rowIndex < 0) continue;
      for (const entry of rowMap.values()) {
        out.push({
          rowIndex,
          rowNumber: rowIndex + 1,
          colKey: entry.colKey,
          header: entry.header,
          message: entry.message,
        });
      }
    }

    return out.sort((a, b) => a.rowIndex - b.rowIndex || a.colKey.localeCompare(b.colKey));
  }

  function emitValidationState(force = false) {
    const errors = getValidationErrors();
    const sig = errors.map((e) => `${e.rowIndex}:${e.colKey}:${e.message}`).join("|");
    if (!force && sig === lastSignature) return;
    lastSignature = sig;

    const detail = {
      engine: ctx.__api || null,
      ok: errors.length === 0,
      invalidCount: errors.length,
      errors,
    };

    try {
      table.dispatchEvent(new CustomEvent("te:validation", { detail }));
    } catch {}

    if (typeof ctx.onValidationChange === "function") {
      try {
        ctx.onValidationChange(detail);
      } catch {}
    }
  }

  function applyResult(rowEl, col, inputEl, result) {
    if (!rowEl || !col?.key) return false;

    if (result.ok) {
      const was = clearStoredError(rowEl, col.key, inputEl);
      setInputValidationDecor(inputEl, { invalid: false });
      return was;
    }

    const rowMap = errorsByRow.get(rowEl) || new Map();
    if (!errorsByRow.has(rowEl)) errorsByRow.set(rowEl, rowMap);

    const prev = rowMap.get(col.key);
    const next = normalizeErrorEntry(col, result.message);
    rowMap.set(col.key, next);

    setInputValidationDecor(inputEl, { invalid: true, message: next.message });

    if (!prev) return true;
    return prev.message !== next.message || prev.header !== next.header;
  }

  function validateCellByRowEl(rowEl, colKey, opts = {}) {
    if (!rowEl) return { ok: true, errors: [] };

    const rowIndex = rowIndexForTr(ctx, rowEl);
    if (!Number.isInteger(rowIndex) || rowIndex < 0) return { ok: true, errors: [] };

    const col = colByKey.get(colKey);
    if (!col || col.kind !== "input" || hiddenCols?.has?.(col.key) || !validationKeys.has(col.key)) {
      const inputEl = ctx.getInput?.(rowEl, colKey) || null;
      const changed = clearStoredError(rowEl, colKey, inputEl);
      if (changed && opts.emit !== false) emitValidationState(false);
      return { ok: true, errors: [] };
    }

    const inputEl = ctx.getInput?.(rowEl, col.key) || null;
    if (!inputEl) {
      const changed = clearStoredError(rowEl, col.key);
      if (changed && opts.emit !== false) emitValidationState(false);
      return { ok: true, errors: [] };
    }

    const result = validateInputEl(ctx, inputEl, col, rowIndex, opts);
    const changed = applyResult(rowEl, col, inputEl, result);
    if (changed && opts.emit !== false) emitValidationState(false);

    return result.ok
      ? { ok: true, errors: [] }
      : { ok: false, errors: [buildError(rowIndex, col, result.message)] };
  }

  function validateCell(rowIndex, colKey, opts = {}) {
    const rows = ctx.getRowEls?.() || [];
    const rowEl = rows[rowIndex] || null;
    return validateCellByRowEl(rowEl, colKey, opts);
  }

  function validateRow(rowIndex, opts = {}) {
    const rows = ctx.getRowEls?.() || [];
    const rowEl = rows[rowIndex] || null;
    if (!rowEl) return { ok: true, errors: [] };

    const rowErrors = [];
    let changed = false;
    const touchedKeys = new Set();

    for (const col of cols) {
      if (!col?.key || col.kind !== "input") continue;
      touchedKeys.add(col.key);
      if (!validationKeys.has(col.key)) continue;
      if (hiddenCols?.has?.(col.key)) continue;
      const inputEl = ctx.getInput?.(rowEl, col.key) || null;
      if (!inputEl) continue;

      const result = validateInputEl(ctx, inputEl, col, rowIndex, opts);
      const wasChanged = applyResult(rowEl, col, inputEl, result);
      if (wasChanged) changed = true;
      if (!result.ok) rowErrors.push(buildError(rowIndex, col, result.message));
    }

    const rowMap = errorsByRow.get(rowEl);
    if (rowMap) {
      for (const [colKey] of rowMap) {
        const col = colByKey.get(colKey);
        if (!col || !touchedKeys.has(colKey) || hiddenCols?.has?.(colKey) || !validationKeys.has(colKey)) {
          rowMap.delete(colKey);
          changed = true;
          const inputEl = ctx.getInput?.(rowEl, colKey);
          if (inputEl) setInputValidationDecor(inputEl, { invalid: false });
        }
      }
      if (!rowMap.size) errorsByRow.delete(rowEl);
    }

    if (changed && opts.emit !== false) emitValidationState(false);
    return { ok: rowErrors.length === 0, errors: rowErrors };
  }

  function validateAll(opts = {}) {
    const rows = ctx.getRowEls?.() || [];
    const seenRows = new Set();
    let changed = false;

    for (let r = 0; r < rows.length; r++) {
      const rowEl = rows[r];
      seenRows.add(rowEl);
      const touchedKeys = new Set();

      for (const col of cols) {
        if (!col?.key || col.kind !== "input") continue;
        touchedKeys.add(col.key);
        if (!validationKeys.has(col.key)) continue;
        if (hiddenCols?.has?.(col.key)) continue;
        const inputEl = ctx.getInput?.(rowEl, col.key) || null;
        if (!inputEl) continue;

        const result = validateInputEl(ctx, inputEl, col, r, opts);
        const wasChanged = applyResult(rowEl, col, inputEl, result);
        if (wasChanged) changed = true;
      }

      const rowMap = errorsByRow.get(rowEl);
      if (!rowMap) continue;

      for (const [colKey] of rowMap) {
        const col = colByKey.get(colKey);
        if (!col || !touchedKeys.has(colKey) || hiddenCols?.has?.(colKey) || !validationKeys.has(colKey)) {
          rowMap.delete(colKey);
          changed = true;
          const inputEl = ctx.getInput?.(rowEl, colKey);
          if (inputEl) setInputValidationDecor(inputEl, { invalid: false });
        }
      }
      if (!rowMap.size) errorsByRow.delete(rowEl);
    }

    for (const [rowEl] of errorsByRow) {
      if (seenRows.has(rowEl)) continue;
      errorsByRow.delete(rowEl);
      changed = true;
    }

    if ((changed || opts.emit === true) && opts.emit !== false) emitValidationState(false);
    const errors = getValidationErrors();
    return { ok: errors.length === 0, errors };
  }

  function clearValidationErrors({ emit = true } = {}) {
    errorsByRow.clear();

    const invalidEls = table && typeof table.querySelectorAll === "function"
      ? table.querySelectorAll(".te-cell-invalid")
      : [];
    invalidEls.forEach((el) => {
      setInputValidationDecor(el, { invalid: false });
    });

    const invalidTds = table && typeof table.querySelectorAll === "function"
      ? table.querySelectorAll("td.te-cell-error")
      : [];
    invalidTds.forEach((td) => {
      td.classList.remove("te-cell-error");
    });

    if (emit) emitValidationState(true);
  }

  function hasValidationErrors() {
    return errorsByRow.size > 0;
  }

  function colIndexForKey(colKey) {
    if (!colKey) return -1;
    if (typeof ctx.colIndexByKey === "function") {
      const idx = Number(ctx.colIndexByKey(colKey));
      if (Number.isFinite(idx) && idx >= 0) return idx;
    }
    for (let i = 0; i < cols.length; i++) {
      if (cols[i]?.key === colKey) return i;
    }
    return -1;
  }

  function getFirstValidationError() {
    const errors = getValidationErrors();
    return errors.length ? errors[0] : null;
  }

  function focusValidationError(error, { edit = false, announce = true } = {}) {
    if (!error || typeof error !== "object") return false;
    const rowIndex = Number.isInteger(error.rowIndex) ? error.rowIndex : Number(error.rowNumber) - 1;
    const colKey = String(error.colKey || "");
    if (!Number.isInteger(rowIndex) || rowIndex < 0 || !colKey) return false;

    const colIndex = colIndexForKey(colKey);
    if (!Number.isInteger(colIndex) || colIndex < 0) return false;

    try {
      ctx.ensureRowVisible?.(rowIndex);
    } catch {}
    try {
      ctx.setCellAnchor?.(rowIndex, colIndex);
    } catch {}
    try {
      ctx.applySelectionVisuals?.();
    } catch {}

    if (edit && typeof ctx.focusEditorAtCell === "function") {
      const focused = !!ctx.focusEditorAtCell(rowIndex, colIndex, { selectAll: true });
      if (!focused) {
        try {
          ctx.focusNavHard?.();
        } catch {}
      }
    } else {
      try {
        ctx.focusNavHard?.();
      } catch {}
    }

    if (announce) {
      const colLabel = String(error.header || colKey);
      try {
        ctx.announceSelectionA11y?.(`Invalid cell: row ${rowIndex + 1}, column ${colLabel}.`);
      } catch {}
    }

    return true;
  }

  function validateForCommit({
    commit = true,
    emit = true,
    focusFirstError = true,
    edit = false,
    announce = true,
    onFail = null,
  } = {}) {
    const res = validateAll({ commit, emit });
    if (res.ok) return { ok: true, errors: [], firstError: null };

    const firstError = (Array.isArray(res.errors) && res.errors.length ? res.errors[0] : null) || getFirstValidationError();
    if (focusFirstError && firstError) focusValidationError(firstError, { edit, announce });

    if (typeof onFail === "function") {
      try {
        onFail(res);
      } catch {}
    }

    return {
      ok: false,
      errors: Array.isArray(res.errors) ? res.errors : [],
      firstError,
    };
  }

  function scheduleReconcileAfterRender() {
    if (_renderReconcilePending) return;
    _renderReconcilePending = true;

    const run = () => {
      _renderReconcilePending = false;
      const changed = pruneErrorStore();
      reapplyDecorFromStore();
      if (changed || errorsByRow.size > 0) emitValidationState(false);
    };

    if (typeof queueMicrotask === "function") queueMicrotask(run);
    else Promise.resolve().then(run);
  }

  function onInputLikeEvent(e, { commit = false } = {}) {
    const el = e?.target;
    if (!el || !el.dataset?.field) return;

    const rowEl = el.closest?.("tr");
    if (!rowEl) return;

    const colKey = String(el.dataset.field || "");
    if (!colKey) return;

    validateCellByRowEl(rowEl, colKey, { commit, emit: true });
  }

  on(table, "input", (e) => onInputLikeEvent(e, { commit: false }));
  on(table, "change", (e) => onInputLikeEvent(e, { commit: true }));
  on(
    table,
    "blur",
    (e) => onInputLikeEvent(e, { commit: true }),
    true,
  );
  on(table, "te:render", () => scheduleReconcileAfterRender());

  ctx.validateCell = validateCell;
  ctx.validateRow = validateRow;
  ctx.validateAll = validateAll;
  ctx.validateForCommit = validateForCommit;
  ctx.hasValidationErrors = hasValidationErrors;
  ctx.getValidationErrors = getValidationErrors;
  ctx.getFirstValidationError = getFirstValidationError;
  ctx.focusValidationError = focusValidationError;
  ctx.clearValidationErrors = clearValidationErrors;
}
